import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CreditCard, Save, Eye, EyeOff, TestTube, CheckCircle2, XCircle,
  Loader2, Building2, AlertCircle, RefreshCw, Edit3,
} from 'lucide-react';
import LogoUpload from './ui-shared/LogoUpload';
import LogoPlacementEditor from './ui-shared/LogoPlacementEditor';
import InvoiceHtmlEditor from './InvoiceHtmlEditor';
import { fetchWithStepUp } from './StepUpModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Editor for the single (UK) Stripe account that powers balance top-ups.
 *  Mirrors the look-and-feel of the CompaniesEditor PayPal credentials
 *  block so the admin's mental model stays consistent. */
export default function StripeSettingsEditor({ token }) {
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );

  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [saved, setSaved]       = useState('');
  const [cfg, setCfg]           = useState(null);
  const [companies, setCompanies] = useState([]);
  const [showSecret, setShowSecret] = useState({ test: false, live: false });
  const [showWhsec, setShowWhsec]   = useState({ test: false, live: false });
  const [pendingSecret, setPendingSecret] = useState({ test: '', live: '' });
  const [pendingWhsec, setPendingWhsec]   = useState({ test: '', live: '' });
  const [testing, setTesting]   = useState({ test: false, live: false });
  const [testResult, setTestResult] = useState({ test: null, live: null });

  const load = useCallback(async () => {
    setLoading(true); setErr(''); setSaved('');
    try {
      const [c, cos] = await Promise.all([
        fetch(`${API_URL}/api/stripe-config`, { headers }).then(async r => {
          if (!r.ok) throw new Error(`stripe-config HTTP ${r.status}`);
          return r.json();
        }),
        fetch(`${API_URL}/api/companies/`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.ok ? r.json() : []),
      ]);
      setCfg(c);
      setCompanies(Array.isArray(cos) ? cos : []);
    } catch (e) {
      setErr(e.message || 'Failed to load Stripe settings');
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => { load(); }, [load]);

  const patch = (updater) => setCfg(prev => ({ ...prev, ...updater(prev) }));
  const patchEnv = (env, updater) => setCfg(prev => ({
    ...prev, [env]: { ...prev[env], ...updater(prev[env] || {}) },
  }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setErr(''); setSaved('');
    try {
      const payload = {
        mode: cfg.mode,
        enabled: cfg.enabled,
        company_id: cfg.company_id || null,
        statement_descriptor: cfg.statement_descriptor || '',
        test: {
          publishable_key: cfg.test?.publishable_key || '',
          secret_key: pendingSecret.test || '',       // empty = keep existing
          webhook_secret: pendingWhsec.test || '',
        },
        live: {
          publishable_key: cfg.live?.publishable_key || '',
          secret_key: pendingSecret.live || '',
          webhook_secret: pendingWhsec.live || '',
        },
      };
      const r = await fetch(`${API_URL}/api/stripe-config`, {
        method: 'PUT', headers, body: JSON.stringify(payload),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setCfg(d);
      setPendingSecret({ test: '', live: '' });
      setPendingWhsec({ test: '', live: '' });
      setSaved('Saved');
      setTimeout(() => setSaved(''), 2500);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (env) => {
    setTesting(t => ({ ...t, [env]: true }));
    setTestResult(r => ({ ...r, [env]: null }));
    try {
      const body = {
        mode: env,
        // If the admin just pasted a new key but hasn't saved yet, test THAT
        // value rather than the stale stored one.
        secret_key: pendingSecret[env] || undefined,
      };
      const r = await fetch(`${API_URL}/api/stripe-config/test`, {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      setTestResult(prev => ({ ...prev, [env]: d }));
    } catch (e) {
      setTestResult(prev => ({ ...prev, [env]: { ok: false, message: e.message || 'Network error' } }));
    } finally {
      setTesting(t => ({ ...t, [env]: false }));
    }
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center gap-2 text-slate-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Stripe settings…
      </div>
    );
  }

  const ukCompany = companies.find(c => c.role === 'UK_COMPANY');

  const EnvBlock = ({ env }) => {
    const section = cfg[env] || {};
    const result = testResult[env];
    const pendingSk = pendingSecret[env];
    const pendingWh = pendingWhsec[env];
    const skLast4 = section.secret_key_last4;
    const whLast4 = section.webhook_secret_last4;
    const liveKey = env === 'live';
    return (
      <div className={`rounded-xl border p-5 ${liveKey ? 'border-emerald-200 bg-emerald-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${liveKey ? 'bg-emerald-600' : 'bg-amber-500'} text-white`}>
              {liveKey ? <CreditCard className="w-4 h-4" /> : <TestTube className="w-4 h-4" />}
            </div>
            <div>
              <p className="font-bold text-slate-800 text-sm uppercase tracking-wider">
                {liveKey ? 'Live credentials' : 'Test credentials'}
              </p>
              <p className="text-xs text-slate-500">
                {liveKey ? 'Real cards are charged.' : 'Sandbox — safe for end-to-end testing.'}
              </p>
            </div>
          </div>
          <button
            onClick={() => runTest(env)}
            disabled={testing[env]}
            data-testid={`stripe-test-${env}-btn`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all flex items-center gap-1.5 ${liveKey ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'} disabled:opacity-50`}
          >
            {testing[env] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube className="w-3.5 h-3.5" />}
            Test connection
          </button>
        </div>

        {/* Publishable key — not sensitive, shown plain. */}
        <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-1">Publishable key</label>
        <input
          type="text"
          value={section.publishable_key || ''}
          onChange={(e) => patchEnv(env, (p) => ({ publishable_key: e.target.value }))}
          placeholder={liveKey ? 'pk_live_…' : 'pk_test_…'}
          data-testid={`stripe-pk-${env}`}
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
        />

        {/* Secret key */}
        <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mt-3 mb-1">
          Secret key {skLast4 && <span className="text-emerald-600 normal-case">· stored · ends in {skLast4}</span>}
        </label>
        <div className="relative">
          <input
            type={showSecret[env] ? 'text' : 'password'}
            value={pendingSk}
            onChange={(e) => setPendingSecret(prev => ({ ...prev, [env]: e.target.value }))}
            placeholder={skLast4 ? 'Leave blank to keep existing' : (liveKey ? 'sk_live_…' : 'sk_test_…')}
            data-testid={`stripe-sk-${env}`}
            className="w-full pr-10 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
          />
          <button
            type="button"
            onClick={() => setShowSecret(s => ({ ...s, [env]: !s[env] }))}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
          >
            {showSecret[env] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {/* Webhook signing secret */}
        <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mt-3 mb-1">
          Webhook signing secret {whLast4 && <span className="text-emerald-600 normal-case">· stored · ends in {whLast4}</span>}
        </label>
        <div className="relative">
          <input
            type={showWhsec[env] ? 'text' : 'password'}
            value={pendingWh}
            onChange={(e) => setPendingWhsec(prev => ({ ...prev, [env]: e.target.value }))}
            placeholder={whLast4 ? 'Leave blank to keep existing' : 'whsec_…'}
            data-testid={`stripe-whsec-${env}`}
            className="w-full pr-10 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono"
          />
          <button
            type="button"
            onClick={() => setShowWhsec(s => ({ ...s, [env]: !s[env] }))}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            tabIndex={-1}
          >
            {showWhsec[env] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>

        {result && (
          <div
            className={`mt-4 p-3 rounded-lg text-xs flex items-start gap-2 ${
              result.ok ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-red-100 text-red-800 border border-red-200'
            }`}
            data-testid={`stripe-test-${env}-result`}
          >
            {result.ok ? <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
            <div>
              <p className="font-bold">{result.ok ? 'Connection OK' : 'Connection failed'}</p>
              <p className="opacity-90">{result.message}</p>
              {result.ok && result.account_id && (
                <p className="mt-1 font-mono text-[11px] opacity-80">
                  account {result.account_id}
                  {typeof result.livemode === 'boolean' && (
                    <span className={`ml-2 px-1.5 py-0.5 rounded ${result.livemode ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'} text-[10px] font-bold`}>
                      {result.livemode ? 'LIVE' : 'TEST'}
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="stripe-settings-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800">Stripe settings</h3>
            <p className="text-xs text-slate-500">One Stripe account powers every balance top-up.</p>
          </div>
        </div>
        <button
          onClick={load}
          className="p-2 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-100"
          title="Reload"
          data-testid="stripe-reload-btn"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {err && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {err}
        </div>
      )}

      {/* Global toggles */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-2">Mode</label>
          <div className="flex bg-slate-100 rounded-lg p-1">
            {['test', 'live'].map(m => (
              <button
                key={m}
                onClick={() => patch(() => ({ mode: m }))}
                data-testid={`stripe-mode-${m}`}
                className={`flex-1 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${cfg.mode === m ? (m === 'live' ? 'bg-emerald-600 text-white shadow' : 'bg-amber-500 text-white shadow') : 'text-slate-600 hover:text-slate-900'}`}
              >
                {m === 'live' ? 'Live' : 'Test'}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Active keys used for new checkouts.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-2">Stripe payments</label>
          <button
            onClick={() => patch((p) => ({ enabled: !p.enabled }))}
            data-testid="stripe-enabled-toggle"
            className={`w-full px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${cfg.enabled ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}
          >
            {cfg.enabled ? 'ENABLED' : 'DISABLED'}
          </button>
          <p className="text-[11px] text-slate-500 mt-2">Master switch — disables Stripe across the app.</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-2">Invoice company</label>
          <select
            value={cfg.company_id || ''}
            onChange={(e) => patch(() => ({ company_id: e.target.value || null }))}
            data-testid="stripe-company-select"
            className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">— Use default —</option>
            {companies.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.role ? ` · ${c.role.replace('_COMPANY','')}` : ''}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-slate-500 mt-2">
            Invoices for Stripe top-ups are issued on this company.
            {ukCompany && !cfg.company_id && (
              <button
                type="button"
                onClick={() => patch(() => ({ company_id: ukCompany.id }))}
                className="ml-1 text-cyan-600 hover:underline font-semibold"
              >
                Set UK company
              </button>
            )}
          </p>
        </div>
      </div>

      {/* Statement descriptor */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider mb-2">
          Statement descriptor <span className="text-slate-400 normal-case">(appears on customer's card statement · max 22 chars)</span>
        </label>
        <input
          type="text"
          value={cfg.statement_descriptor || ''}
          onChange={(e) => patch(() => ({ statement_descriptor: e.target.value.slice(0, 22) }))}
          placeholder="AUTO-REMAP"
          maxLength={22}
          data-testid="stripe-statement-descriptor"
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 font-mono uppercase"
        />
      </div>

      {/* Inline editor for the company used on Stripe invoices.
          Lets the admin tweak the legal/billing data (name, address,
          VAT) AND upload a logo without leaving the Stripe page. Edits
          go straight to the same `/api/companies/{id}` endpoint that
          the CompaniesEditor uses, so the data is shared. */}
      <StripeInvoiceCompanyPanel
        key={cfg.company_id || 'none'}
        token={token}
        companyId={cfg.company_id}
        onCompanyUpdated={(updated) => {
          // Reflect the change in the local list so the picker label
          // refreshes without a full reload.
          setCompanies(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
        }}
      />

      {/* Credential blocks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <EnvBlock env="test" />
        <EnvBlock env="live" />
      </div>

      {/* Webhook url hint */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 flex items-start gap-3">
        <Building2 className="w-5 h-5 text-cyan-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 space-y-1 flex-1 min-w-0">
          <p className="font-semibold text-cyan-900">Stripe webhook URL</p>
          <p className="text-xs">
            Add this endpoint in your Stripe dashboard (Developers → Webhooks → Add endpoint):
          </p>
          <code className="block mt-1 p-2 bg-white border border-slate-200 rounded font-mono text-xs text-slate-800 select-all break-all">
            {`${window.location.origin}/api/stripe/webhook`}
          </code>
          <WebhookReachabilityTester />
          <p className="text-[11px] text-slate-500 mt-1">
            Events to listen for: <code className="font-mono">checkout.session.completed</code>,
            <code className="font-mono"> checkout.session.async_payment_succeeded</code>,
            <code className="font-mono"> checkout.session.async_payment_failed</code>,
            <code className="font-mono"> checkout.session.expired</code>.
            After saving the endpoint, reveal its signing secret (<code className="font-mono">whsec_…</code>) and paste it above.
          </p>
        </div>
      </div>

      {/* Save */}
      <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-200">
        {saved && (
          <span className="text-emerald-600 text-sm font-semibold flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4" /> {saved}
          </span>
        )}
        <button
          onClick={save}
          disabled={saving}
          data-testid="stripe-save-btn"
          className="px-5 py-2.5 bg-gradient-to-r from-violet-500 to-indigo-600 text-white rounded-lg font-bold text-sm hover:shadow-lg active:scale-[0.99] transition-all flex items-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save settings
        </button>
      </div>

      {/* Full HTML invoice template editor (Stripe — single global) */}
      <div className="pt-4 mt-4 border-t border-slate-200">
        <InvoiceHtmlEditor
          token={token}
          title="Stripe invoice template"
          subtitle="Single template used for ALL Stripe-issued invoices. English only."
          loadUrl={`${API_URL}/api/config/stripe-invoice-template-html`}
          saveUrl={`${API_URL}/api/config/stripe-invoice-template-html`}
          previewUrl={`${API_URL}/api/config/stripe-invoice-html-preview`}
          showEngineToggle
        />
      </div>
    </div>
  );
}

/**
 * Inline editor for the company record used as the issuer on
 * Stripe-funded invoices. Lets the admin tweak name / address /
 * VAT / phone / bank-details AND upload an in-database logo without
 * leaving the Stripe settings page. Writes to the same
 * `/api/companies/{id}` endpoint that the standalone CompaniesEditor
 * uses, so changes round-trip across both screens.
 */
function StripeInvoiceCompanyPanel({ token, companyId, onCompanyUpdated }) {
  const headers = React.useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token],
  );
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');
  const [draft, setDraft] = useState(null);

  const openEditor = async () => {
    if (!companyId) return;
    setLoading(true); setErr('');
    try {
      const r = await fetch(`${API_URL}/api/companies/`, { headers });
      const list = await r.json();
      const found = (Array.isArray(list) ? list : []).find(c => c.id === companyId);
      if (!found) throw new Error('Company not found');
      setDraft(found);
      setOpen(true);
    } catch (e) {
      setErr(e.message || 'Failed to load company');
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true); setErr(''); setSaved('');
    try {
      // Slim payload — only the invoice-relevant fields. Avoids
      // accidentally clobbering PayPal credentials which live on the
      // same record but are managed by CompaniesEditor.
      const body = {
        name: draft.name || '',
        company_number: draft.company_number || '',
        vat: draft.vat || '',
        address: draft.address || '',
        city: draft.city || '',
        country: draft.country || '',
        phone: draft.phone || '',
        email: draft.email || '',
        website: draft.website || '',
        bank_details: draft.bank_details || '',
        logo_data_url: draft.logo_data_url || '',
        // Persist the logo-placement sliders alongside billing fields.
        invoice_settings: draft.invoice_settings || {},
        // If the admin uploaded a new inline logo, drop the old URL so
        // the PDF generator picks the new image up.
        ...(draft.logo_data_url ? { logo_url: '' } : {}),
        invoice_prefix: draft.invoice_prefix || 'INV',
      };
      const r = await fetchWithStepUp(`${API_URL}/api/companies/${companyId}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error((d && d.detail) || `HTTP ${r.status}`);
      setSaved('Saved'); setTimeout(() => setSaved(''), 2500);
      onCompanyUpdated?.(d);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!companyId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-sm" data-testid="stripe-invoice-company-empty">
        <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-amber-900">
          Pick an <b>Invoice company</b> above to edit its billing details
          (name, address, VAT) and upload a logo for the Stripe receipt.
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="stripe-invoice-company-panel">
      <div className="flex items-center justify-between gap-3">
        <div>
          <label className="block text-[11px] uppercase font-bold text-slate-600 tracking-wider">
            Stripe invoice details
          </label>
          <p className="text-xs text-slate-500 mt-0.5">
            Name, address, VAT and logo that will appear on every Stripe-funded invoice PDF.
          </p>
        </div>
        {!open && (
          <button
            type="button"
            onClick={openEditor}
            disabled={loading}
            data-testid="stripe-invoice-edit-btn"
            className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-violet-500 to-indigo-600 text-white hover:shadow-md flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Edit3 className="w-3.5 h-3.5" />}
            Edit details & logo
          </button>
        )}
      </div>

      {open && draft && (
        <div className="space-y-3 pt-2 border-t border-slate-100">
          <LogoUpload
            value={draft.logo_data_url || draft.logo_url || ''}
            onChange={(dataUrl) => setDraft({ ...draft, logo_data_url: dataUrl, logo_url: '' })}
            testid="stripe-invoice-logo"
          />
          {/* Logo placement controls + live PDF preview. The preview
              reads the SAVED state, so admins should hit "Save invoice
              details" once before tweaking sliders if it's a brand-new
              company. */}
          <LogoPlacementEditor
            companyId={companyId}
            token={token}
            value={draft.invoice_settings}
            onChange={(next) => setDraft({ ...draft, invoice_settings: next })}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              ['name', 'Company name', 'AUTO-REMAP LTD'],
              ['company_number', 'Company number', '12569942'],
              ['vat', 'VAT number', 'GB123456789'],
              ['address', 'Address', '85 Great Portland Street'],
              ['city', 'City', 'London'],
              ['country', 'Country / postcode', 'United Kingdom, W1W 7LT'],
              ['phone', 'Phone', '+44 ...'],
              ['email', 'Email', 'invoices@example.com'],
              ['website', 'Website', 'https://auto-remap.com'],
              ['invoice_prefix', 'Invoice prefix', 'INV'],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">{label}</label>
                <input
                  type="text"
                  value={draft[k] || ''}
                  onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                  placeholder={ph}
                  data-testid={`stripe-invoice-${k}`}
                  className="w-full px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="block text-[11px] font-bold text-slate-700 mb-1">Bank details / payment instructions</label>
            <textarea
              rows={3}
              value={draft.bank_details || ''}
              onChange={(e) => setDraft({ ...draft, bank_details: e.target.value })}
              placeholder={'Bank: ...\nIBAN: ...\nSWIFT/BIC: ...'}
              data-testid="stripe-invoice-bank_details"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
            />
          </div>
          {err && (
            <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            {saved && (
              <span className="text-emerald-600 text-sm font-semibold flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> {saved}
              </span>
            )}
            <button
              type="button"
              onClick={() => { setOpen(false); setErr(''); }}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              data-testid="stripe-invoice-save"
              className="px-4 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:shadow-md disabled:opacity-50 flex items-center gap-1.5"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save invoice details
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * WebhookReachabilityTester — small "Test webhook reachability" button
 * that GETs the current webhook URL and reports whether nginx +
 * backend are wired correctly.
 *
 * Stripe's real webhook handler accepts ONLY POST, so a GET probe
 * returns HTTP 405 ("Method Not Allowed") when everything works.
 * That counter-intuitive 405 is THE signal we want — it proves:
 *   1. DNS resolves the domain ✅
 *   2. HTTPS / SSL handshake succeeds ✅
 *   3. nginx is proxying `/api/*` to the FastAPI backend ✅
 *   4. The `/api/stripe/webhook` route is registered ✅
 * Any other outcome surfaces a specific failure mode the admin can
 * give to the devops engineer.
 */
function WebhookReachabilityTester() {
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const url = `${window.location.origin}/api/stripe/webhook`;

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const t0 = performance.now();
      const r = await fetch(url, { method: 'GET', cache: 'no-store' });
      const ms = Math.round(performance.now() - t0);
      if (r.status === 405) {
        // Expected — the endpoint exists but only accepts POST.
        setResult({ ok: true, msg: `Reachable (HTTP 405 Method Not Allowed — that's the expected response for a GET probe; Stripe will POST and it will work).`, latency: ms });
      } else if (r.status === 404) {
        setResult({ ok: false, msg: `HTTP 404 — nginx is NOT proxying /api/* to the FastAPI backend, or the route isn't registered. Check nginx config.`, latency: ms });
      } else if (r.status === 502 || r.status === 503 || r.status === 504) {
        setResult({ ok: false, msg: `HTTP ${r.status} — nginx reached but the FastAPI backend is down or unhealthy. Check supervisor status of the backend service.`, latency: ms });
      } else if (r.status >= 200 && r.status < 300) {
        setResult({ ok: true, msg: `HTTP ${r.status} — reachable. Stripe will POST and the handler will validate the signature.`, latency: ms });
      } else {
        setResult({ ok: false, msg: `Unexpected HTTP ${r.status}. Endpoint reachable but returned something we didn't expect.`, latency: ms });
      }
    } catch (e) {
      // TypeError("Failed to fetch") = DNS / TLS / network failure.
      setResult({
        ok: false,
        msg: `Network error: ${e.message}. Likely causes: DNS not resolving, HTTPS certificate invalid, or the server isn't accepting connections on port 443.`,
        latency: null,
      });
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-2" data-testid="webhook-reachability">
      <button
        type="button"
        onClick={run}
        disabled={busy}
        data-testid="webhook-reachability-btn"
        className="px-2.5 py-1 rounded text-[11px] font-bold bg-cyan-600 text-white hover:bg-cyan-700 disabled:opacity-50 inline-flex items-center gap-1.5"
      >
        {busy ? '⏳ Testing…' : '🩺 Test webhook reachability'}
      </button>
      {result && (
        <div
          className={`mt-1.5 px-2.5 py-1.5 rounded border text-[11px] ${
            result.ok
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
          data-testid={`webhook-reachability-${result.ok ? 'ok' : 'fail'}`}
        >
          <div className="font-semibold">{result.ok ? '✅ OK' : '❌ Not reachable'}{result.latency != null && ` · ${result.latency}ms`}</div>
          <div className="mt-0.5 leading-snug">{result.msg}</div>
        </div>
      )}
    </div>
  );
}
